import { useState, useEffect } from 'react';
import { User } from '../types';
import { getUsers, createUser, updateUser, deleteUser } from '../services';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al obtener usuarios';
      setError(errorMessage);
      console.error('Error en useUsers:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addUser = async (userData: { email: string; password: string; role: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      await createUser(userData);
      await fetchUsers(); // Actualiza la lista después de crear
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al crear usuario';
      setError(errorMessage);
      console.error('Error al crear usuario:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const editUser = async (id: number, userData: { email?: string; password?: string; role?: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      await updateUser(id, userData);
      await fetchUsers(); // Actualiza la lista después de editar
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al actualizar usuario';
      setError(errorMessage);
      console.error('Error al actualizar usuario:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (id: number) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteUser(id);
      await fetchUsers(); // Actualiza la lista después de eliminar
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al eliminar usuario';
      setError(errorMessage);
      console.error('Error al eliminar usuario:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users,
    loading,
    error,
    fetchUsers,
    addUser,
    editUser,
    removeUser
  };
}; 